// THE color registry — see docs/COLOR_SYSTEM_PLAN.md.
//
// One file defines every color the product has. The Brand Manager UI, the Studio preview, the
// tenant site's stylesheet, the look presets and the AI's style tools all PROJECT from this list;
// nothing downstream invents a color name.
//
// Why it exists: the same conceptual color was defined in up to five places (the default palette
// in 5, the dark surface in 4, readableTextOn in 3), the Brand Manager offered 13 colors of which
// 6 reached a site, and TWO different functions mapped a brand onto a site with different rules —
// so the same brand produced different colors depending on which path applied it.
//
// Three invariants, each enforced by a test (apps/builder/src/lib/colorRoles.test.ts):
//   1. One role ⇄ one CSS var. No lossy translation layer.
//   2. Every role has a real consumer. A color that paints nothing may not be added — the rule
//      that prevents another "Button Primary BG does nothing".
//   3. Every role states what it paints (`controls`), and the UI copy is GENERATED from that, so
//      a label can never drift from behavior.
//
// Consumed as source by apps/builder (vite + vitest alias) and apps/tenant-sites (file: dep).
// services/api-gateway builds standalone with no access to packages/, so it carries a mirror at
// src/lib/colorRoles.ts kept honest by a drift test (colorRoles.mirror.test.ts) — not by hope.

export type ColorGroup = 'Core' | 'Surfaces' | 'Text' | 'Buttons' | 'Links';

export interface ColorRole {
  /** Storage key — `brand_colors.role`. Stable; renaming one is a migration. */
  role: string;
  /** The ONE CSS custom property this role emits. */
  cssVar: string;
  /** Human label, as the Brand Manager shows it. */
  label: string;
  group: ColorGroup;
  /**
   * What this color actually paints, in a tenant's words. The Brand Manager renders this under
   * the label, so it must describe REAL consumers — if it lists something, a component reads
   * this var for that thing.
   */
  controls: string[];
  /**
   * The value when the tenant has never set this role: another role's key, or
   * `readableOn:<role>` for a computed-contrast text color. Undefined = falls back to the
   * platform palette. This is what keeps 15 slots survivable for someone who only wants to
   * pick two colors — the slot still exists and is still editable.
   */
  derivesFrom?: string;
  /** The element in the Brand Preview this role paints (Phase 2 hover-linking). */
  previewTarget: string;
  /**
   * The legacy `site_style_settings` column, where one exists. Roles without a column are
   * carried in `colors_json` (Phase 1). Note that `page_background`/`surface` are NEW brand
   * roles that map onto columns which already existed — the columns were always there; the
   * brand vocabulary simply had no word for them, which is why look presets had to carry their
   * own background/surface hexes and the brand could never reach them.
   */
  styleColumn?: string;
}

export const COLOR_ROLES: ColorRole[] = [
  // ── Core ──────────────────────────────────────────────────────────────────
  {
    role: 'primary',
    cssVar: '--color-primary',
    label: 'Primary',
    group: 'Core',
    controls: ['call-to-action bands', 'active states', 'listing prices'],
    previewTarget: 'header',
    styleColumn: 'color_primary',
  },
  {
    role: 'secondary',
    cssVar: '--color-secondary',
    label: 'Secondary',
    group: 'Core',
    controls: ['the hero band', 'the footer, unless you set a footer color'],
    previewTarget: 'hero',
    styleColumn: 'color_secondary',
  },
  {
    role: 'accent',
    cssVar: '--color-accent',
    label: 'Accent',
    group: 'Core',
    controls: ['highlights', 'badges', 'a look’s feature band'],
    previewTarget: 'badge',
    styleColumn: 'color_accent',
  },

  // ── Surfaces ──────────────────────────────────────────────────────────────
  {
    role: 'page_background',
    cssVar: '--color-background',
    label: 'Page background',
    group: 'Surfaces',
    controls: ['the page behind everything, outside any card or panel'],
    // Matches what the server has always done when a site has no style row of its own
    // (routes/style.ts's resolveBrandColors). See the plan's §7 review flag.
    derivesFrom: 'neutral_light',
    previewTarget: 'page',
    styleColumn: 'color_background',
  },
  {
    role: 'surface',
    cssVar: '--color-surface',
    label: 'Panel & card background',
    group: 'Surfaces',
    controls: ['listing cards', 'agent cards', 'search result rows', 'the hero search bar'],
    derivesFrom: 'neutral_light',
    previewTarget: 'card',
    styleColumn: 'color_surface',
  },
  {
    role: 'neutral_dark',
    cssVar: '--color-neutral-dark',
    label: 'Neutral Dark',
    group: 'Surfaces',
    controls: ['dark section bands'],
    previewTarget: 'band-dark',
  },
  {
    role: 'neutral_light',
    cssVar: '--color-neutral-light',
    label: 'Neutral Light',
    group: 'Surfaces',
    controls: ['light section bands'],
    previewTarget: 'band-light',
  },
  {
    role: 'footer',
    cssVar: '--color-footer-bg',
    label: 'Footer background',
    group: 'Surfaces',
    controls: ['the footer band at the bottom of every page'],
    derivesFrom: 'secondary',
    previewTarget: 'footer',
  },

  // ── Text ──────────────────────────────────────────────────────────────────
  {
    role: 'text_primary',
    cssVar: '--color-text',
    label: 'Text Primary',
    group: 'Text',
    controls: ['body copy', 'headings'],
    previewTarget: 'heading',
    styleColumn: 'color_text',
  },
  {
    role: 'text_secondary',
    cssVar: '--color-text-muted',
    label: 'Text Secondary',
    group: 'Text',
    controls: ['captions', 'listing metadata', 'muted labels'],
    previewTarget: 'body',
    styleColumn: 'color_text_muted',
  },
  {
    /**
     * The other half of Text Primary. Text Primary is what words look like on WHITE; this is
     * what they look like everywhere the system has decided the background is too dark for it.
     *
     * It existed already — as a hardcoded `#FFFFFF` inside `readableTextOn`, chosen for every
     * dark band, every dark footer and every hero photo, and reachable from no control. A brand
     * whose type is a warm off-white or a pale stone had no way to say so: the Brand Manager
     * offered two text colours, both of which only ever applied on light surfaces.
     *
     * Left alone it is still white, so nothing changes for a site that has not asked.
     */
    role: 'text_on_dark',
    cssVar: '--color-text-on-dark',
    label: 'Text on dark',
    group: 'Text',
    controls: ['words on dark section bands', 'text over hero photos', 'a dark footer’s text'],
    previewTarget: 'band-dark',
  },

  // ── Buttons ───────────────────────────────────────────────────────────────
  {
    role: 'button_primary_bg',
    cssVar: '--color-button-primary-bg',
    label: 'Button Primary BG',
    group: 'Buttons',
    controls: ['button fills', 'the search button', 'form submit buttons'],
    derivesFrom: 'primary',
    previewTarget: 'cta-button',
  },
  {
    role: 'button_primary_text',
    cssVar: '--color-button-primary-text',
    label: 'Button Primary Text',
    group: 'Buttons',
    controls: ['the label inside a button'],
    derivesFrom: 'readableOn:button_primary_bg',
    previewTarget: 'cta-button',
  },
  {
    role: 'button_primary_hover',
    cssVar: '--color-button-primary-hover',
    label: 'Button Primary Hover',
    group: 'Buttons',
    controls: ['a button’s fill while the pointer is over it'],
    derivesFrom: 'button_primary_bg',
    previewTarget: 'cta-button',
  },

  // ── Links ─────────────────────────────────────────────────────────────────
  {
    role: 'link_color',
    cssVar: '--color-link',
    label: 'Link Color',
    group: 'Links',
    controls: ['inline links in your page copy'],
    derivesFrom: 'primary',
    previewTarget: 'link',
  },
  {
    role: 'link_hover',
    cssVar: '--color-link-hover',
    label: 'Link Hover',
    group: 'Links',
    controls: ['an inline link while the pointer is over it'],
    derivesFrom: 'link_color',
    previewTarget: 'link',
  },
];

/**
 * Computed, never authored: readable text ON the primary/secondary brand surfaces. These are
 * emitted as CSS vars but are not brand roles — a tenant cannot set them, because getting them
 * wrong is how white text lands on a light-tan footer ("fix the footer, I can't see the words").
 */
/** The readable-text var for a role, e.g. 'neutral_dark' → '--color-on-neutral-dark'. */
export function onCssVar(role: string): string {
  return `--color-on-${role.replace(/_/g, '-')}`;
}

/**
 * One on-colour per role, not a curated list of the two someone happened to need.
 *
 * It WAS a curated list — on-primary and on-secondary — which meant a section band painted from
 * any other role had no readable text to fall back to, and look presets froze a literal
 * `textColor: '#ffffff'` next to their background instead. That hex is half of the bug this
 * phase exists to fix: white text pinned beside a background that later follows the brand is how
 * you get white-on-cream. Generating the whole set costs fifteen short declarations and removes
 * the question of which roles deserve one.
 */
export const DERIVED_CSS_VARS: readonly string[] = COLOR_ROLES.map((r) => onCssVar(r.role));

// ── Lookups ─────────────────────────────────────────────────────────────────

const BY_ROLE = new Map(COLOR_ROLES.map((r) => [r.role, r]));
const BY_VAR = new Map(COLOR_ROLES.map((r) => [r.cssVar, r]));

export const roleByKey = (role: string): ColorRole | undefined => BY_ROLE.get(role);
export const roleByCssVar = (cssVar: string): ColorRole | undefined => BY_VAR.get(cssVar);
export const rolesInGroup = (group: ColorGroup): ColorRole[] =>
  COLOR_ROLES.filter((r) => r.group === group);

/** Role → hex. The shape a brand's palette takes everywhere in this system. */
export type Palette = Record<string, string>;

// ── The one default palette ─────────────────────────────────────────────────

/**
 * THE platform default palette (BLT brand guidelines). Previously duplicated in five places —
 * site-style-tokens.ts, routes/style.ts, site-config/defaults.ts, globals.css and the var
 * fallbacks in packages/blocks/theme.ts — which had already drifted: the muted grey was
 * #888195 in three, #6E6680 in CSS and #6b7280 in the block renderer.
 *
 * #6E6680 is the value kept, deliberately: it is the one someone darkened to pass WCAG AA and
 * then never back-ported. Unifying on the lighter value would have been a silent accessibility
 * regression on every site that uses the default.
 */
export const PLATFORM_PALETTE: Palette = {
  primary: '#4E1F60',          // Deep Plum
  secondary: '#10032A',        // Midnight Plum
  accent: '#C9B8F4',           // Lavender Mist
  page_background: '#FFFFFF',
  surface: '#F8F6FF',          // faint lavender tint
  neutral_dark: '#10032A',
  neutral_light: '#F8F6FF',
  footer: '#10032A',
  text_primary: '#10032A',
  text_secondary: '#6E6680',   // WCAG-AA corrected; see above
  // White, because that is the literal it replaces: the hardcoded `#FFFFFF` that `readableTextOn`
  // returned for every dark surface. A site that never touches this role renders exactly as it
  // did before the role existed.
  text_on_dark: '#FFFFFF',
  button_primary_bg: '#4E1F60',
  button_primary_text: '#FFFFFF',
  button_primary_hover: '#281C3F',
  link_color: '#4E1F60',
  link_hover: '#281C3F',
};

/**
 * THE dark-surface override, applied when a page's resolved dark mode is on. Previously four
 * copies (tenant layout, builder colorContrast, Noir's own preset, and a divergent one in
 * globals.css whose text was #e6e8ec instead of #e8eaed).
 */
export const DARK_SURFACE: Palette = {
  page_background: '#0f1115',
  surface: '#181b21',
  text_primary: '#e8eaed',
  text_secondary: '#9aa2ad',
};

/** DARK_SURFACE as the CSS vars the renderers spread. */

// ── Contrast ────────────────────────────────────────────────────────────────

const BRAND_INK = '#10032A'; // Midnight Plum — the dark-on-light choice (stays on-brand)

/** Relative luminance (WCAG) of a #rgb / #rrggbb hex, or null if unparseable. */
export function luminance(hex: string): number | null {
  const h = (hex || '').replace('#', '').trim();
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const ch = (i: number) => {
    const c = parseInt(full.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * ch(0) + 0.7152 * ch(2) + 0.0722 * ch(4);
}

/**
 * Readable text color for a given background hex. Defaults to white on dark, brand ink on light.
 *
 * White vs bg contrast = 1.05/(L+0.05); ink vs bg = (L+0.05)/0.05. They cross near L≈0.18, but
 * this biases toward white (most brands are mid/dark) and only flips to ink on genuinely light
 * brands. The 0.42 threshold was identical in all three previous copies (builder, tenant-sites,
 * api-gateway) — this move is a pure de-duplication, not a behavior change.
 *
 * `onDark` is the tenant's `text_on_dark` role, and it is the whole reason that role exists: the
 * light branch here WAS a hardcoded white, applied to every dark band, dark footer and hero
 * photo on the site, with no control anywhere that could change it. A brand whose type is a warm
 * off-white could set two text colours in the Brand Manager and neither of them would apply
 * anywhere the background was dark.
 *
 * Only the dark branch is authorable. The light branch stays brand ink on purpose: it is the
 * guaranteed-legible fallback, and letting it follow a tenant's choice is how you get pale text
 * on a pale band with nothing to catch it.
 */
export function readableTextOn(bg: string | null | undefined, onDark?: string | null): string {
  // Deliberately not the `isHex` helper below: that is a `const`, and DARK_SURFACE_VARS calls
  // this function at MODULE LOAD, so depending on it would be a temporal dead zone — the same
  // module-init trap that blanked this file once already when BRAND_INK was declared too late.
  const light =
    typeof onDark === 'string' && /^#[0-9a-fA-F]{6}$/.test(onDark.trim()) ? onDark.trim() : '#FFFFFF';
  const L = luminance(bg ?? '');
  if (L === null) return light;
  return L > 0.42 ? BRAND_INK : light;
}

/**
 * Pick the right logo VARIANT for a background by its luminance — the brand-manager rule "a light
 * logo goes on a dark background, a dark logo on a light one, automatically." `filterClass` is a
 * Tailwind recolor applied ONLY when we fall back to the wrong-tone logo so it stays visible
 * (brightness-0 → black on light; brightness-0 invert → white on dark). Returns null if no logo.
 *
 *   primary  = the dark-on-light logo (brand.logo_url)
 *   reversed = the light-on-dark logo (brand.logo_dark_url)
 *
 * Moved here from apps/tenant-sites so the footer designs in @realsite/blocks — which the Studio
 * preview renders too — pick the same logo the live site does.
 */
export function logoForBackground(
  bg: string,
  logos: { primary?: string | null; reversed?: string | null },
): { url: string; filterClass: string } | null {
  const L = luminance(bg);
  const darkBg = L === null ? true : L < 0.42; // unknown / hero / footer backgrounds are treated as dark
  const primary = logos.primary || null;
  const reversed = logos.reversed || null;
  if (darkBg) {
    if (reversed) return { url: reversed, filterClass: '' };
    if (primary) return { url: primary, filterClass: 'brightness-0 invert' }; // whiten the dark logo
  } else {
    if (primary) return { url: primary, filterClass: '' };
    if (reversed) return { url: reversed, filterClass: 'brightness-0' }; // darken the light logo
  }
  return null;
}

/**
 * The dark-surface var set, INCLUDING the recomputed on-colours for the roles it overrides.
 *
 * It used to be `paletteToCssVars(DARK_SURFACE)` alone — four role vars and nothing else. That
 * left `--color-on-background` and friends holding the values computed from the LIGHT brand, so a
 * section painted from an overridden role could get text chosen for a background it no longer
 * has. Overriding a colour without re-deriving what reads on it is the same mistake as freezing
 * a hex: correct at the moment it was written, wrong from the next change onward.
 */
export const DARK_SURFACE_VARS: Record<string, string> = {
  ...paletteToCssVars(DARK_SURFACE),
  ...Object.fromEntries(
    Object.entries(DARK_SURFACE).map(([role, hex]) => {
      const on = readableTextOn(hex);
      return [
        onCssVar(role),
        // A `var()` reference rather than the computed hex, because this set is a module-level
        // CONSTANT — built once, with no brand in hand, and spread inline onto the page wrapper.
        // Referencing the role lets a tenant's `text_on_dark` reach dark mode too, and the
        // fallback is the literal this always emitted, so an untouched site is unchanged.
        //
        // ONLY where the answer is the light one. Two of dark mode's four roles are themselves
        // near-white (its text colours), so their on-colour is INK — text sitting ON the pale
        // swatch, not on the dark page. Wrapping those too would put the tenant's off-white on a
        // near-white background: invisible, and computed by the very helper that exists to stop
        // that happening.
        on === '#FFFFFF' ? `var(--color-text-on-dark, ${on})` : on,
      ];
    }),
  ),
};

// ── Resolution ──────────────────────────────────────────────────────────────

const isHex = (v: unknown): v is string =>
  typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v.trim());

/**
 * Fill every role: what the tenant set, else what it derives from, else the platform default.
 *
 * Derivation is a DEFAULT, not a replacement for control — the slot stays editable. Chains
 * (link_hover → link_color → primary) resolve in registry order with a visited-set so a
 * mis-authored cycle degrades to the platform value instead of hanging.
 *
 * `fillDefaults: false` returns ONLY the roles that trace back to something the brand actually
 * authored. That distinction is load-bearing for the brand→site write path: applying a brand
 * that defines just a primary must not also stamp the platform's secondary/text/surface over
 * whatever that site already had. Rendering wants the opposite (every var must resolve), which
 * is why it stays the default.
 */
export function resolvePalette(
  authored: Palette | null | undefined,
  opts: { fillDefaults?: boolean } = {},
): Palette {
  const fillDefaults = opts.fillDefaults !== false;
  const src: Palette = {};
  for (const [k, v] of Object.entries(authored ?? {})) if (isHex(v)) src[k] = v.trim();

  const out: Palette = {};
  /** The resolved hex, or undefined when the chain bottoms out with nothing authored and
   *  `fillDefaults` is off — see the note on that option below. */
  const resolve = (role: string, seen: Set<string>): string | undefined => {
    if (out[role]) return out[role];
    if (src[role]) return (out[role] = src[role]);
    const from = BY_ROLE.get(role)?.derivesFrom;
    if (from && !seen.has(role)) {
      seen.add(role);
      const base = resolve(from.replace(/^readableOn:/, ''), seen);
      if (base !== undefined) {
        // `readableOn:` picks against the tenant's own text_on_dark, so a button label derived
        // for a dark fill uses the same off-white as every other dark surface on the site rather
        // than a white this file chose. Safe to resolve inline: text_on_dark derives from
        // nothing, so it cannot recurse back into the role being resolved.
        const value = from.startsWith('readableOn:')
          ? readableTextOn(base, src.text_on_dark ?? (fillDefaults ? PLATFORM_PALETTE.text_on_dark : undefined))
          : base;
        return (out[role] = value);
      }
    }
    if (!fillDefaults) return undefined;
    return (out[role] = PLATFORM_PALETTE[role] ?? '#000000');
  };

  for (const def of COLOR_ROLES) resolve(def.role, new Set());
  return out;
}

/** A palette → the CSS custom properties the renderers emit. Roles only; see DERIVED_CSS_VARS. */
export function paletteToCssVars(palette: Palette): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const def of COLOR_ROLES) {
    const hex = palette[def.role];
    if (hex) vars[def.cssVar] = hex;
  }
  return vars;
}

/**
 * The FULL var set a themed surface needs: every role, plus the computed on-colors.
 *
 * Both renderers (apps/tenant-sites' (public)/layout.tsx and apps/builder's colorContrast
 * buildThemeVars) build from this one function, so the Studio preview and the live site cannot
 * emit different var sets — they did, by hand, and that is why the preview could lie.
 */
export function themeCssVars(authored: Palette | null | undefined): Record<string, string> {
  const palette = resolvePalette(authored);
  const vars: Record<string, string> = { ...paletteToCssVars(palette) };
  // One on-colour per role, computed from that role's luminance. A band painted from any role
  // now has readable text without the preset pinning a literal white beside it — and where that
  // text lands on a DARK role it is the tenant's `text_on_dark`, so "the words on my dark
  // sections" is one control rather than a hardcoded white in a contrast helper.
  for (const def of COLOR_ROLES) {
    vars[onCssVar(def.role)] = readableTextOn(palette[def.role], palette.text_on_dark);
  }
  return vars;
}

// ── Brand → site ────────────────────────────────────────────────────────────

/**
 * THE brand-palette → legacy `site_style_settings` column mapping.
 *
 * Replaces two divergent implementations: apps/builder's mapBrandToSiteStyle (6 roles,
 * neutral_light → surface only, never wrote background) and api-gateway routes/style.ts's
 * resolveBrandColors (7 roles, neutral_light → background AND surface, neutral_dark as a text
 * fallback). The same brand produced different site colors depending on which ran.
 *
 * Unified on the SUPERSET — the server's semantics — per docs/COLOR_SYSTEM_PLAN.md §7: it is
 * what most recently-rendered sites already resolved through, and a superset drops no role.
 * The one behavior change is that applying a brand from the builder now also sets the page
 * background (from page_background, which derives from neutral_light) instead of leaving it at
 * the platform default.
 *
 * Roles with no legacy column ride in `colors_json` (Phase 1) — nothing is dropped any more.
 */
export function brandPaletteToStyleColumns(authored: Palette | null | undefined): Record<string, string> {
  // authored-only: never write a column the brand has no opinion about (see resolvePalette).
  const palette = resolvePalette(authored, { fillDefaults: false });
  const columns: Record<string, string> = {};
  for (const def of COLOR_ROLES) {
    if (def.styleColumn && palette[def.role]) columns[def.styleColumn] = palette[def.role];
  }
  return columns;
}

/** Legacy `site_style_settings` columns → a role palette (the read direction). */
/**
 * Parse an AUTHORED palette out of a JSON blob — `site_style_settings.colors_json`, which holds
 * the roles that have no column of their own (migration 099).
 *
 * Tolerant on the way in, strict on the way out: accepts a JSON string or an already-parsed
 * object (mysql2 hands back either depending on how it sees the column), and keeps only 6-digit
 * hex values. These end up inside a `style="…"` attribute on a tenant's live site, so whatever
 * happens to be sitting in the column is not something to pass through.
 *
 * Unknown keys survive deliberately — a role removed from the registry should not silently
 * destroy a tenant's stored value, and resolvePalette ignores what it does not recognise.
 */
export function parsePalette(raw: unknown): Palette {
  if (!raw) return {};
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  const out: Palette = {};
  for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
    if (isHex(v)) out[k] = String(v).trim();
  }
  return out;
}

export function styleColumnsToPalette(row: Record<string, unknown> | null | undefined): Palette {
  const palette: Palette = {};
  if (!row) return palette;
  for (const def of COLOR_ROLES) {
    if (!def.styleColumn) continue;
    const v = row[def.styleColumn];
    if (isHex(v)) palette[def.role] = String(v).trim();
  }
  return palette;
}
